import {
  BarChart3,
  BookMarked,
  BookOpen,
  Building2,
  Chrome,
  ConciergeBell,
  ExternalLink,
  FolderKanban,
  FolderTree,
  ImageIcon,
  LayoutDashboard,
  LineChart,
  Link2,
  Menu,
  Newspaper,
  PanelBottom,
  Settings2,
  UserCircle,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { NavLink } from 'react-router-dom'

import { getLocalizedPath } from '@/content/i18n'
import { dashboardCopy } from '@/content/dashboardContent'
import { useAuth } from '@/hooks/useAuth'
import { cn, getLocalizedValue } from '@/lib/utils'
import type { Locale } from '@/types/site'

type DashboardSidebarProps = {
  locale: Locale
  onNavigate?: () => void
}

type NavLabel =
  | 'navProfile'
  | 'navDashboard'
  | 'navUsers'
  | 'navContentHome'
  | 'navArticles'
  | 'navProjects'
  | 'navProjectCategories'
  | 'navServices'
  | 'navResources'
  | 'navClientLogos'
  | 'navSeo'
  | 'navAnalytics'
  | 'navNavigation'
  | 'navFooter'
  | 'navMedia'
  | 'navSettings'
  | 'navSettingsGoogle'
  | 'navSettingsTracking'

type NavItem = {
  to: string
  labelKey: NavLabel
  icon: LucideIcon
}

type SectionTitle = 'sectionOverview' | 'sectionContent' | 'sectionSeo' | 'sectionSite' | 'sectionSettings'

const profileOnly: NavItem[] = [{ to: 'profile', labelKey: 'navProfile', icon: UserCircle }]

const overview: NavItem[] = [
  { to: '', labelKey: 'navDashboard', icon: LayoutDashboard },
  { to: 'users', labelKey: 'navUsers', icon: Users },
]

const content: NavItem[] = [
  { to: 'content/home', labelKey: 'navContentHome', icon: BookOpen },
  { to: 'articles', labelKey: 'navArticles', icon: Newspaper },
  { to: 'projects', labelKey: 'navProjects', icon: FolderKanban },
  { to: 'project-categories', labelKey: 'navProjectCategories', icon: FolderTree },
  { to: 'services', labelKey: 'navServices', icon: ConciergeBell },
  { to: 'resources', labelKey: 'navResources', icon: BookMarked },
  { to: 'client-logos', labelKey: 'navClientLogos', icon: Building2 },
]

const seoAnalytics: NavItem[] = [
  { to: 'seo', labelKey: 'navSeo', icon: Link2 },
  { to: 'analytics', labelKey: 'navAnalytics', icon: BarChart3 },
]

const site: NavItem[] = [
  { to: 'navigation', labelKey: 'navNavigation', icon: Menu },
  { to: 'footer', labelKey: 'navFooter', icon: PanelBottom },
  { to: 'media', labelKey: 'navMedia', icon: ImageIcon },
]

const settings: NavItem[] = [
  { to: 'profile', labelKey: 'navProfile', icon: UserCircle },
  { to: 'settings', labelKey: 'navSettings', icon: Settings2 },
  { to: 'settings/google', labelKey: 'navSettingsGoogle', icon: Chrome },
  { to: 'settings/tracking', labelKey: 'navSettingsTracking', icon: LineChart },
]

function NavGroup({
  title,
  items,
  locale,
  base,
  onNavigate,
}: {
  title: SectionTitle
  items: NavItem[]
  locale: Locale
  base: string
  onNavigate?: () => void
}) {
  return (
    <div className="mb-5">
      <p className="mb-2 px-3 text-start text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {getLocalizedValue(dashboardCopy[title], locale)}
      </p>
      <div className="flex flex-col gap-1">
        {items.map(({ to, labelKey, icon: Icon }) => {
          const path = to ? `${base}/${to}` : base
          return (
            <NavLink
              key={to || 'index'}
              to={path}
              end={to === '' || to === 'settings' || to === 'profile'}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex w-full items-center justify-start gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-semibold transition-colors',
                  isActive
                    ? 'bg-dash-accent/10 text-dash-accent shadow-sm ring-1 ring-dash-accent/25'
                    : 'text-slate-600 hover:bg-white/60 hover:text-slate-900',
                )
              }
            >
              <Icon className="h-5 w-5 shrink-0" aria-hidden />
              {getLocalizedValue(dashboardCopy[labelKey], locale)}
            </NavLink>
          )
        })}
      </div>
    </div>
  )
}

export function DashboardSidebar({ locale, onNavigate }: DashboardSidebarProps) {
  const { user } = useAuth()
  const isAdmin = user?.is_admin ?? false
  const homePath = getLocalizedPath(locale)
  const base = getLocalizedPath(locale, 'dashboard')

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <nav
        className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        aria-label={getLocalizedValue(dashboardCopy.title, locale)}
      >
        {isAdmin ? (
          <>
            <NavGroup title="sectionOverview" items={overview} locale={locale} base={base} onNavigate={onNavigate} />
            <NavGroup title="sectionContent" items={content} locale={locale} base={base} onNavigate={onNavigate} />
            <NavGroup title="sectionSeo" items={seoAnalytics} locale={locale} base={base} onNavigate={onNavigate} />
            <NavGroup title="sectionSite" items={site} locale={locale} base={base} onNavigate={onNavigate} />
            <NavGroup title="sectionSettings" items={settings} locale={locale} base={base} onNavigate={onNavigate} />
          </>
        ) : (
          <NavGroup title="sectionSettings" items={profileOnly} locale={locale} base={base} onNavigate={onNavigate} />
        )}
      </nav>

      <div className="mt-auto border-t border-slate-200/80 pt-4">
        <a
          href={homePath}
          className="flex w-full items-center justify-start gap-2 rounded-xl px-3 py-2.5 text-start text-sm font-medium text-slate-600 transition-colors hover:bg-white/60 hover:text-slate-900"
          onClick={onNavigate}
        >
          <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
          {getLocalizedValue(dashboardCopy.backToSite, locale)}
        </a>
      </div>
    </div>
  )
}
